/**
 * Admin-Karte „Projekt-Prüfungen“ (PV-052, A1/A4): Zeitplan, letzter Lauf mit Kennzahlen
 * und Fehlern, Hand-Start, Regel-Liste mit Schaltern. Ausschalten = pausieren (A4).
 * Float-Verbindung (L4): Dienst-Zugang zum Float-MCP.
 */
import type {
  BefundRegelKey,
  DienstStatus,
  UpdateBefundPruefungEinstellungenInput,
} from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/toast.js';
import { BEFUNDE_QUERY_KEY, useBefundLocale } from '../components/v2/befunde/befund-basis.js';
import { useConfirm } from '../components/v2/confirm-dialog.js';
import { ApiError, api } from '../lib/api.js';

const EINSTELLUNGEN_KEY = ['admin-befund-einstellungen'] as const;
const REGELN_KEY = ['admin-befund-regeln'] as const;
const FLOAT_KEY = ['admin-dienst-float'] as const;
// Anzeige Mo…So; Werte nach JS Date.getDay() (0=So … 6=Sa).
const TAG_REIHENFOLGE = [1, 2, 3, 4, 5, 6, 0] as const;

export function ProjektPruefungenAdmin() {
  const { t } = useTranslation();
  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">
          {t('admin.befunde_titel', 'Projekt-Prüfungen')}
        </h2>
        <p className="text-xs text-[color:var(--ink-3)] mt-0.5">
          {t(
            'admin.befunde_untertitel',
            'Regelmäßiger Prüflauf, der Befunde an Projekten anlegt und erledigt. Eine Regel auszuschalten pausiert ihre Befunde nur.',
          )}
        </p>
      </div>
      <ZeitplanUndLauf />
      <FloatVerbindung />
      <RegelListe />
    </section>
  );
}

function ZeitplanUndLauf() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: EINSTELLUNGEN_KEY,
    queryFn: api.getBefundEinstellungen,
    // Während ein Lauf läuft, alle 5 s nachsehen — danach nicht mehr.
    refetchInterval: (q) => (q.state.data?.lauf_laeuft_seit ? 5000 : false),
  });
  const [draft, setDraft] = useState<UpdateBefundPruefungEinstellungenInput | null>(null);
  useEffect(() => {
    if (query.data && draft === null) {
      const { enabled, time, weekdays } = query.data;
      setDraft({ enabled, time, weekdays });
    }
  }, [query.data, draft]);

  const speichern = useMutation({
    mutationFn: (body: UpdateBefundPruefungEinstellungenInput) =>
      api.updateBefundEinstellungen(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EINSTELLUNGEN_KEY });
      toast.success(t('admin.befunde_gespeichert', 'Zeitplan gespeichert'));
    },
    onError: (err) =>
      toast.error(
        t('admin.befunde_fehler', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      ),
  });
  const lauf = useMutation({
    mutationFn: () => api.starteBefundLauf(),
    onSuccess: () => {
      toast.success(t('admin.befunde_gestartet', 'Prüflauf gestartet'));
      void queryClient.invalidateQueries({ queryKey: EINSTELLUNGEN_KEY });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.push({
          kind: 'info',
          title: t('admin.befunde_laeuft_bereits', 'Es läuft bereits ein Prüflauf.'),
        });
        return;
      }
      toast.error(
        t('admin.befunde_lauf_fehler', 'Start fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      );
    },
  });

  const zeit = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(
          new Date(iso),
        )
      : '–';
  const tagLabel: Record<number, string> = {
    0: t('admin.weekday_short_sun', 'So'),
    1: t('admin.weekday_short_mon', 'Mo'),
    2: t('admin.weekday_short_tue', 'Di'),
    3: t('admin.weekday_short_wed', 'Mi'),
    4: t('admin.weekday_short_thu', 'Do'),
    5: t('admin.weekday_short_fri', 'Fr'),
    6: t('admin.weekday_short_sat', 'Sa'),
  };
  const d = query.data;
  const erg = d?.letzter_lauf_ergebnis ?? null;

  return (
    <div className="rounded-lg border border-[color:var(--line)] bg-[var(--surface)] p-4 space-y-4">
      {draft !== null && (
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
            {t('admin.befunde_aktiv', 'Aktiv')}
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) =>
                setDraft((x) => (x === null ? x : { ...x, enabled: e.target.checked }))
              }
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
            {t('admin.befunde_uhrzeit', 'Uhrzeit')}
            <input
              type="time"
              value={draft.time}
              onChange={(e) => setDraft((x) => (x === null ? x : { ...x, time: e.target.value }))}
              className="rounded-md border border-[color:var(--line)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[color:var(--ink)]"
            />
            {d && <span className="text-[color:var(--ink-4)]">({d.timezone})</span>}
          </label>
          <div className="flex items-center gap-2 text-xs text-[color:var(--ink-3)]">
            {t('admin.befunde_tage', 'Prüftage')}
            <div className="flex gap-1">
              {TAG_REIHENFOLGE.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  aria-pressed={draft.weekdays.includes(tag)}
                  onClick={() =>
                    setDraft((x) =>
                      x === null
                        ? x
                        : {
                            ...x,
                            weekdays: x.weekdays.includes(tag)
                              ? x.weekdays.filter((w) => w !== tag)
                              : [...x.weekdays, tag],
                          },
                    )
                  }
                  className={`rounded-md border px-2 py-1 text-xs ${
                    draft.weekdays.includes(tag)
                      ? 'border-[color:var(--primary)] bg-[var(--primary)] font-medium text-white'
                      : 'border-[color:var(--line)] bg-[var(--surface)] text-[color:var(--ink-3)]'
                  }`}
                >
                  {tagLabel[tag]}
                </button>
              ))}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {draft.weekdays.length === 0 && (
              <span className="text-xs text-[color:var(--warn,#b45309)]">
                {t(
                  'admin.befunde_kein_tag',
                  'Mindestens ein Prüftag nötig — zum Abschalten den Aktiv-Schalter nutzen.',
                )}
              </span>
            )}
            <button
              type="button"
              onClick={() => speichern.mutate(draft)}
              disabled={speichern.isPending || draft.weekdays.length === 0}
              className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {t('admin.befunde_speichern', 'Speichern')}
            </button>
          </div>
        </div>
      )}

      <div className="border-t border-[color:var(--line)] pt-3 flex flex-wrap items-start gap-4">
        <div className="space-y-1 text-sm text-[color:var(--ink-2)]">
          <div className="text-xs uppercase tracking-wide text-[color:var(--ink-3)]">
            {t('admin.befunde_letzter_lauf', 'Letzter Lauf')}
          </div>
          {d?.lauf_laeuft_seit && (
            <div>
              {t('admin.befunde_laeuft_seit', 'Läuft seit {{zeit}}', {
                zeit: zeit(d.lauf_laeuft_seit),
              })}
            </div>
          )}
          {!d?.letzter_lauf_start ? (
            <div>{t('admin.befunde_nie', 'Noch kein Lauf.')}</div>
          ) : (
            <>
              <div>
                {t('admin.befunde_lauf_zeit', '{{start}} – {{ende}}', {
                  start: zeit(d.letzter_lauf_start),
                  ende: zeit(d.letzter_lauf_ende),
                })}
              </div>
              {erg && (
                <div>
                  {t(
                    'admin.befunde_lauf_kennzahlen',
                    '{{neu}} neu · {{erledigt}} erledigt · {{offen}} offen · {{final}} final gesetzt',
                    {
                      neu: erg.neu,
                      erledigt: erg.erledigt,
                      offen: erg.offen,
                      final: erg.final_gesetzt,
                    },
                  )}
                </div>
              )}
              {/* PV-052 L3: nur zeigen, wenn es etwas zu sagen gibt — Entzug ist die Ausnahme. */}
              {erg && erg.final_entzogen > 0 && (
                <div>
                  {t(
                    'admin.befunde_lauf_entzogen',
                    '{{n}} × „final“ entzogen (Buchung nach SF-Ende)',
                    { n: erg.final_entzogen },
                  )}
                </div>
              )}
              {/* PV-052 L4: Float-Ausfall ganz oder teilweise sichtbar (AK 3). */}
              {erg && erg.float_ok === false && (
                <div className="text-[color:var(--danger)]">
                  {t(
                    'admin.befunde_float_ausfall',
                    'Float nicht abrufbar ({{grund}}) — Float- und Budget-Befunde unverändert.',
                    {
                      grund: t(
                        `admin.float_grund_${erg.float_status ?? 'nicht_erreichbar'}`,
                        erg.float_status ?? '',
                      ),
                    },
                  )}
                </div>
              )}
              {erg && erg.float_ok === true && (
                <div
                  className={
                    erg.float_fehler_projekte > 0 ? 'text-[color:var(--danger)]' : undefined
                  }
                >
                  {erg.float_fehler_projekte > 0
                    ? t(
                        'admin.befunde_float_teilweise',
                        'Float: {{n}} Projekte abgerufen, {{m}} nicht abrufbar — deren Befunde unverändert.',
                        { n: erg.float_projekte, m: erg.float_fehler_projekte },
                      )
                    : t('admin.befunde_float_ok', 'Float: {{n}} Projekte abgerufen', {
                        n: erg.float_projekte,
                      })}
                </div>
              )}
              {erg && erg.fehler.length > 0 && (
                <div className="text-[color:var(--danger)]">
                  <div className="text-xs">{t('admin.befunde_lauf_fehler_titel', 'Fehler')}</div>
                  <ul className="text-xs">
                    {erg.fehler.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => lauf.mutate()}
          disabled={lauf.isPending || !!d?.lauf_laeuft_seit}
          className="ml-auto rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm disabled:opacity-50"
        >
          {t('admin.befunde_jetzt', 'Jetzt ausführen')}
        </button>
      </div>
    </div>
  );
}

function RegelListe() {
  const { t, i18n } = useTranslation();
  const locale = useBefundLocale();
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: [...REGELN_KEY, locale],
    queryFn: () => api.listBefundRegeln(locale),
  });
  const schalten = useMutation({
    mutationFn: ({ regel, aktiv }: { regel: BefundRegelKey; aktiv: boolean }) =>
      api.setBefundRegelAktiv(regel, aktiv),
    onSuccess: (_d, v) => {
      toast.success(
        v.aktiv
          ? t('admin.befunde_regel_an', 'Regel eingeschaltet')
          : t('admin.befunde_regel_aus', 'Regel pausiert'),
      );
      void queryClient.invalidateQueries({ queryKey: REGELN_KEY });
      // Ausgeschaltet verschwinden die Befunde sofort aus allen Ansichten (A4).
      void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });
    },
    onError: (err) =>
      toast.error(
        t('admin.befunde_fehler', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      ),
  });
  const items = query.data?.items ?? [];
  return (
    <div className="rounded-lg border border-[color:var(--line)] bg-[var(--surface)] divide-y divide-[color:var(--line)]">
      <div className="px-4 py-2 text-xs uppercase tracking-wide text-[color:var(--ink-3)]">
        {t('admin.befunde_regeln', 'Regeln')}
      </div>
      {items.map((r) => (
        <div key={r.regel} className="px-4 py-3 flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[color:var(--ink)]">{r.titel}</div>
            <div className="text-xs text-[color:var(--ink-3)]">{r.beschreibung}</div>
            <div className="text-xs text-[color:var(--ink-4)] mt-1">
              {r.geaendert_von_name && r.geaendert_am
                ? t('admin.befunde_geschaltet', 'zuletzt geschaltet von {{name}} am {{datum}}', {
                    name: r.geaendert_von_name,
                    datum: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
                      new Date(r.geaendert_am),
                    ),
                  })
                : t('admin.befunde_nie_geschaltet', 'noch nie geschaltet')}
            </div>
          </div>
          <span className="text-xs text-[color:var(--ink-3)] w-20 text-right">
            {t('admin.befunde_offen', '{{count}} offen', { count: r.offen })}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={r.aktiv}
            aria-label={r.titel}
            disabled={schalten.isPending}
            onClick={() => schalten.mutate({ regel: r.regel, aktiv: !r.aktiv })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              r.aktiv ? 'bg-[var(--primary)]' : 'bg-[var(--surface-3)]'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-[var(--surface)] shadow-sm transition-transform ${
                r.aktiv ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      ))}
    </div>
  );
}

function FloatVerbindung() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const query = useQuery({ queryKey: FLOAT_KEY, queryFn: api.getFloatDienst });

  // Rücksprung aus dem Login: Ergebnis einmal melden, Parameter aus der Adresse nehmen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ergebnis = params.get('float');
    if (!ergebnis) return;
    if (ergebnis === 'verbunden') toast.success(t('admin.float_verbunden_ok', 'Float verbunden'));
    else toast.error(t('admin.float_verbinden_fehler', 'Float-Verbindung fehlgeschlagen'));
    params.delete('float');
    const rest = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
  }, [t, toast]);

  const testen = useMutation({
    mutationFn: () => api.testeFloatDienst(),
    onSuccess: (r) =>
      toast.success(t('admin.float_test_ok', 'Float antwortet ({{n}} Werkzeuge)', { n: r.tools })),
    onError: (err) =>
      toast.error(
        t('admin.float_test_fehler', 'Float antwortet nicht'),
        err instanceof Error ? err.message : undefined,
      ),
  });
  const trennen = useMutation({
    mutationFn: () => api.trenneFloatDienst(),
    onSuccess: () => {
      toast.success(t('admin.float_getrennt', 'Float-Verbindung getrennt'));
      void queryClient.invalidateQueries({ queryKey: FLOAT_KEY });
    },
    onError: (err) =>
      toast.error(
        t('admin.befunde_fehler', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      ),
  });

  const d = query.data;
  const statusText: Record<DienstStatus['status'], string> = {
    verbunden: t('admin.float_status_verbunden', 'Verbunden'),
    widerrufen: t(
      'admin.float_status_widerrufen',
      'Zugang widerrufen — bitte neu verbinden. Bis dahin bleiben Float- und Budget-Befunde unverändert.',
    ),
    nicht_verbunden: t('admin.float_status_nicht_verbunden', 'Nicht verbunden'),
    andere_adresse: t(
      'admin.float_status_andere_adresse',
      'Verbunden mit einer anderen Adresse — bitte neu verbinden.',
    ),
    nicht_erreichbar: t(
      'admin.float_status_nicht_erreichbar',
      'Verbunden, aber der Anmeldedienst antwortet gerade nicht.',
    ),
    nicht_konfiguriert: t(
      'admin.float_status_nicht_konfiguriert',
      'Nicht eingerichtet (FLOAT_MCP_URL fehlt).',
    ),
  };
  const warnen = d && d.status !== 'verbunden' && d.status !== 'nicht_konfiguriert';

  return (
    <div className="rounded-lg border border-[color:var(--line)] bg-[var(--surface)] p-4 space-y-2">
      <div className="text-xs uppercase tracking-wide text-[color:var(--ink-3)]">
        {t('admin.float_titel', 'Float-Verbindung')}
      </div>
      {query.isError ? (
        <p className="text-sm text-[color:var(--danger)]">
          {t('admin.float_laden_fehler', 'Float-Verbindung konnte nicht geladen werden.')}
        </p>
      ) : !d ? (
        <p className="text-sm text-[color:var(--ink-3)]">{t('admin.float_laedt', 'Lädt…')}</p>
      ) : (
        <div className="flex flex-wrap items-start gap-4">
          <div className="space-y-1 text-sm text-[color:var(--ink-2)] flex-1 min-w-0">
            <div className={warnen ? 'text-[color:var(--danger)]' : undefined}>
              {statusText[d.status]}
            </div>
            {d.verbunden_von_name && d.verbunden_am && (
              <div className="text-[color:var(--ink-3)]">
                {t('admin.float_verbunden_von', 'Hergestellt von {{name}} am {{datum}}', {
                  name: d.verbunden_von_name,
                  datum: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
                    new Date(d.verbunden_am),
                  ),
                })}
              </div>
            )}
            {d.mcp_url && <div className="text-[color:var(--ink-3)] break-all">{d.mcp_url}</div>}
          </div>
          {d.status !== 'nicht_konfiguriert' && (
            <div className="flex gap-2">
              <a
                href={api.floatConnectUrl()}
                className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-white"
              >
                {d.status === 'verbunden'
                  ? t('admin.float_neu_verbinden', 'Neu verbinden')
                  : t('admin.float_verbinden', 'Verbinden')}
              </a>
              {d.status === 'verbunden' && (
                <button
                  type="button"
                  onClick={() => testen.mutate()}
                  disabled={testen.isPending}
                  className="rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  {t('admin.float_testen', 'Verbindung testen')}
                </button>
              )}
              {d.status !== 'nicht_verbunden' && (
                <button
                  type="button"
                  disabled={trennen.isPending}
                  onClick={async () => {
                    const ja = await confirmAction({
                      title: t('admin.float_trennen_frage', 'Float-Verbindung trennen?'),
                      body: t(
                        'admin.float_trennen_text',
                        'Danach bleiben Float- und Budget-Befunde unverändert, bis wieder jemand verbindet.',
                      ),
                      confirmLabel: t('admin.float_trennen_ja', 'Trennen bestätigen'),
                      tone: 'danger',
                    });
                    if (ja) trennen.mutate();
                  }}
                  className="rounded-md border border-[color:var(--line)] px-3 py-1.5 text-sm text-[color:var(--danger)] disabled:opacity-50"
                >
                  {t('admin.float_trennen', 'Trennen')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
